import React, { useState, useEffect } from 'react';
import { fromIso } from '../../utils/businessDate';
import { getTenantToday } from '../../utils/dateUtils';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Link2, Search } from 'lucide-react';

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '1rem' : '2rem'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: none;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4); }
  ` : `
    background: transparent;
    color: #94a3b8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    &:hover { border-color: #ef4444; color: white; }
  `}
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ContextBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 8px;
  background: rgba(14, 116, 144, 0.12);
  color: #cbd5e1;
`;

const ContextTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: white;
  font-weight: 700;
`;

const SearchBox = styled.label`
  width: min(32rem, 100%);
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.8rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.72);
  color: #94a3b8;
`;

const SearchInput = styled.input`
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  color: white;
  font: inherit;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid ${props => props.$color || '#ef4444'};
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const SituationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 1.5rem;
`;

const SituationCard = styled(motion.div)<{ $type: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid ${props => props.$type === 'CHAPTER_11' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)'};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: ${props => props.$type === 'CHAPTER_11' ? '#ef4444' : '#f59e0b'};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
`;

const SituationHeader = styled.div<{ $type: string }>`
  padding: 1.25rem;
  background: ${props => props.$type === 'CHAPTER_11' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CompanyName = styled.div`
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
`;

const TypeBadge = styled.span<{ $type: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;

  ${props => {
    switch (props.$type) {
      case 'CHAPTER_11':
        return 'background: rgba(239, 68, 68, 0.1); color: #ef4444; border: 1px solid rgba(239, 68, 68, 0.2);';
      case 'CHAPTER_7':
        return 'background: rgba(239, 68, 68, 0.2); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.3);';
      case 'OUT_OF_COURT':
        return 'background: rgba(245, 158, 11, 0.1); color: #f59e0b; border: 1px solid rgba(245, 158, 11, 0.2);';
      case 'EXCHANGE_OFFER':
        return 'background: rgba(59, 130, 246, 0.1); color: #3b82f6; border: 1px solid rgba(59, 130, 246, 0.2);';
      default:
        return 'background: rgba(148, 163, 184, 0.1); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.2);';
    }
  }}
`;

const SituationBody = styled.div`
  padding: 1.25rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div``;

const InfoLabel = styled.div`
  color: #64748b;
  font-size: 0.7rem;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const InfoValue = styled.div<{ $highlight?: boolean }>`
  color: ${props => props.$highlight ? '#ef4444' : 'white'};
  font-size: 0.9rem;
  font-weight: ${props => props.$highlight ? '700' : '500'};
`;

const DeadlineSection = styled.div<{ $urgent: boolean }>`
  background: ${props => props.$urgent ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid ${props => props.$urgent ? 'rgba(239, 68, 68, 0.2)' : 'transparent'};
`;

const DeadlineTitle = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DeadlineDate = styled.div<{ $urgent: boolean }>`
  color: ${props => props.$urgent ? '#ef4444' : 'white'};
  font-size: 1rem;
  font-weight: 600;
`;

const DeadlineDescription = styled.div`
  color: #94a3b8;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const ClaimsPreview = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ClaimBadge = styled.span<{ $type: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => {
    if (props.$type === 'SECURED') return 'rgba(16, 185, 129, 0.15)';
    if (props.$type === 'SENIOR_UNSECURED') return 'rgba(59, 130, 246, 0.15)';
    if (props.$type === 'SUBORDINATED') return 'rgba(139, 92, 246, 0.15)';
    return 'rgba(245, 158, 11, 0.15)';
  }};
  color: ${props => {
    if (props.$type === 'SECURED') return '#10b981';
    if (props.$type === 'SENIOR_UNSECURED') return '#3b82f6';
    if (props.$type === 'SUBORDINATED') return '#8b5cf6';
    return '#f59e0b';
  }};
`;

interface DistressedSituation {
  id: number;
  companyName: string;
  situationType: string;
  status: string;
  filingDate: string;
  courtName: string;
  caseNumber: string;
  totalDebt: number;
  ourExposure: number;
  estimatedRecovery: number;
  nextDeadline: string;
  deadlineDescription: string;
  dipFinancing: boolean;
  dipAmount: number;
  claims: { type: string; amount: number; recoveryEstimate: number }[];
}

interface DistressedDebtScreenProps {
  embedded?: boolean;
}

const DistressedDebtScreen: React.FC<DistressedDebtScreenProps> = ({ embedded = false }) => {
  const [situations, setSituations] = useState<DistressedSituation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const contextBorrower = searchParams.get('borrower') || '';
  const contextLoanId = searchParams.get('loanId');
  const contextWorkoutCaseId = searchParams.get('workoutCaseId');
  const contextSituationId = searchParams.get('distressedSituationId');
  const [query, setQuery] = useState(contextBorrower);

  const openDetail = (id: number) => {
    const p = new URLSearchParams(searchParams);
    p.set('sub', 'distressed-detail');
    p.set('id', String(id));
    setSearchParams(p, { replace: false });
  };

  useEffect(() => {
    if (!contextSituationId) return;
    const id = Number(contextSituationId);
    if (Number.isFinite(id) && id > 0) openDetail(id);
    // Route once when the contextual handoff first mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextSituationId]);

  useEffect(() => {
    const fetchSituations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get('/api/credit/distressed');
        // Ensure we always set an array
        const data = response.data;
        setSituations(Array.isArray(data) ? data : (data?.situations || data?.content || []));
      } catch (error) {
        console.error('Failed to fetch distressed situations:', error);
        setError('Failed to load distressed situations. Please try again.');
        setSituations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSituations();
  }, []);

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '$0';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  };

  const isDeadlineUrgent = (deadline: string | undefined | null) => {
    if (!deadline) return false;
    const deadlineDate = new Date(deadline);
    const today = fromIso(getTenantToday());
    const diffDays = Math.ceil((deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 14;
  };

  const allSituations = Array.isArray(situations) ? situations : [];
  const normalizedQuery = query.trim().toLowerCase();
  const safeSituations = normalizedQuery
    ? allSituations.filter(s => [s.companyName, s.caseNumber, s.status, s.situationType]
        .some(value => String(value || '').toLowerCase().includes(normalizedQuery)))
    : allSituations;
  const totalExposure = safeSituations.reduce((sum, s) => sum + (s.ourExposure || 0), 0);
  const chapter11Count = safeSituations.filter(s => s.situationType === 'CHAPTER_11').length;
  const urgentDeadlines = safeSituations.filter(s => isDeadlineUrgent(s.nextDeadline)).length;

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>
            <span>⚠️</span>
            Distressed Debt
          </Title>
          <Actions>
            <Button $variant="secondary">Calendar View</Button>
            <Button $variant="primary">+ New Situation</Button>
          </Actions>
        </Header>
      )}

      {error && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}

      {(contextBorrower || contextLoanId || contextWorkoutCaseId) && (
        <ContextBanner>
          <div>
            <ContextTitle><Link2 size={16} /> Loan 360 workout handoff</ContextTitle>
            <div>
              {contextBorrower || 'Selected loan'}
              {contextLoanId ? ` · loan ${contextLoanId}` : ''}
              {contextWorkoutCaseId ? ` · workout case ${contextWorkoutCaseId}` : ''}
            </div>
          </div>
          <div>{contextSituationId ? 'Opening linked situation' : 'No linked distressed situation'}</div>
        </ContextBanner>
      )}

      <SearchBox>
        <Search size={16} />
        <SearchInput
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Search borrower, case, status, or situation type"
          aria-label="Search distressed situations"
        />
      </SearchBox>

      <StatsRow>
        <StatCard $color="#ef4444">
          <StatLabel>Total Exposure</StatLabel>
          <StatValue>{formatCurrency(totalExposure)}</StatValue>
        </StatCard>
        <StatCard $color="#f59e0b">
          <StatLabel>Active Situations</StatLabel>
          <StatValue>{safeSituations.length}</StatValue>
        </StatCard>
        <StatCard $color="#8b5cf6">
          <StatLabel>Chapter 11</StatLabel>
          <StatValue>{chapter11Count}</StatValue>
        </StatCard>
        <StatCard $color="#dc2626">
          <StatLabel>Urgent Deadlines</StatLabel>
          <StatValue>{urgentDeadlines}</StatValue>
        </StatCard>
      </StatsRow>

      <SituationsGrid>
        {!loading && safeSituations.length === 0 && (
          <div style={{ color: '#94a3b8', padding: '1rem 0' }}>
            No distressed situations match this loan context or search.
          </div>
        )}
        {safeSituations.map(situation => (
          <SituationCard
            key={situation.id}
            $type={situation.situationType || 'UNKNOWN'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            onClick={() => openDetail(situation.id)}
            style={{ cursor: 'pointer' }}
          >
            <SituationHeader $type={situation.situationType || 'UNKNOWN'}>
              <CompanyName>{situation.companyName || 'Unknown Company'}</CompanyName>
              <TypeBadge $type={situation.situationType || 'UNKNOWN'}>
                {(situation.situationType || 'UNKNOWN').replace(/_/g, ' ')}
              </TypeBadge>
            </SituationHeader>
            <SituationBody>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Our Exposure</InfoLabel>
                  <InfoValue $highlight>{formatCurrency(situation.ourExposure)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Est. Recovery</InfoLabel>
                  <InfoValue>{situation.estimatedRecovery || 0}%</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Total Debt</InfoLabel>
                  <InfoValue>{formatCurrency(situation.totalDebt)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Filing Date</InfoLabel>
                  <InfoValue>{situation.filingDate ? new Date(situation.filingDate).toLocaleDateString() : '-'}</InfoValue>
                </InfoItem>
              </InfoGrid>

              <DeadlineSection $urgent={isDeadlineUrgent(situation.nextDeadline)}>
                <DeadlineTitle>
                  {isDeadlineUrgent(situation.nextDeadline) ? '🚨' : '📅'} Next Deadline
                </DeadlineTitle>
                <DeadlineDate $urgent={isDeadlineUrgent(situation.nextDeadline)}>
                  {situation.nextDeadline ? new Date(situation.nextDeadline).toLocaleDateString() : '-'}
                </DeadlineDate>
                <DeadlineDescription>{situation.deadlineDescription || ''}</DeadlineDescription>
              </DeadlineSection>

              {situation.dipFinancing && (
                <InfoItem style={{ marginBottom: '1rem' }}>
                  <InfoLabel>DIP Financing</InfoLabel>
                  <InfoValue>{formatCurrency(situation.dipAmount)}</InfoValue>
                </InfoItem>
              )}

              <ClaimsPreview>
                {(situation.claims || []).map((claim, idx) => (
                  <ClaimBadge key={idx} $type={claim.type || 'UNKNOWN'}>
                    {(claim.type || 'UNKNOWN').replace(/_/g, ' ')}: {formatCurrency(claim.amount)} ({claim.recoveryEstimate || 0}%)
                  </ClaimBadge>
                ))}
              </ClaimsPreview>
            </SituationBody>
          </SituationCard>
        ))}
      </SituationsGrid>
    </Container>
  );
};

export default DistressedDebtScreen;
